import Card from 'components/card';
import { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { getBiProspects, Granularity } from 'interfaces/bi';
import { formatNumber, formatPercent } from './helpers';

const GRANULARITIES: Array<{ label: string; value: Granularity }> = [
    { label: 'Jour', value: 'day' },
    { label: 'Semaine', value: 'week' },
    { label: 'Mois', value: 'month' },
    { label: 'Année', value: 'year' },
];

/**
 * KPI #5 & #6 — Prospect funnel + transformation rate.
 *
 * Combo chart: bars for prospects created vs converted per bucket, plus a line
 * for the per-bucket transformation rate. Headline stats show the overall
 * totals and conversion rate. Granularity is selectable (day/week/month/year).
 * Reads from `/bi/prospects`.
 */
function ProspectFunnel(props: { from: string; to: string }) {
    const { from, to } = props;
    const [granularity, setGranularity] = useState<Granularity>('month');
    const [rows, setRows] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            setLoading(true);
            const res = await getBiProspects({ from, to, granularity, page: 1, limit: 366 });
            if (!active) return;
            setRows(res?.data ?? []);
            setSummary(res?.summary ?? null);
            setLoading(false);
        })();
        return () => {
            active = false;
        };
    }, [from, to, granularity]);

    const series = useMemo(
        () => [
            { name: 'Prospects', type: 'column', data: rows.map((r) => r.created || 0) },
            { name: 'Convertis', type: 'column', data: rows.map((r) => r.converted || 0) },
            {
                name: 'Taux de transfo.',
                type: 'line',
                data: rows.map((r) => Number(r.conversionRate) || 0),
            },
        ],
        [rows],
    );

    const options: any = {
        chart: { type: 'line', toolbar: { show: false }, stacked: false },
        colors: ['#0260CB', '#39B8FF', '#22c55e'],
        stroke: { width: [0, 0, 3], curve: 'smooth' },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
        dataLabels: { enabled: false },
        xaxis: {
            categories: rows.map((r) => r.period),
            labels: { style: { colors: '#A3AED0', fontSize: '10px' }, rotate: -45 },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: [
            {
                title: { text: 'Nombre', style: { color: '#A3AED0', fontSize: '10px' } },
                labels: { style: { colors: '#A3AED0', fontSize: '10px' } },
            },
            {
                opposite: true,
                title: { text: 'Taux (%)', style: { color: '#A3AED0', fontSize: '10px' } },
                labels: {
                    style: { colors: '#A3AED0', fontSize: '10px' },
                    formatter: (v: number) => `${Math.round(v)}%`,
                },
                min: 0,
                max: 100,
            },
        ],
        tooltip: {
            theme: 'dark',
            y: {
                formatter: (v: number, opts: any) =>
                    opts?.seriesIndex === 2 ? formatPercent(v) : formatNumber(v),
            },
        },
        legend: { show: true, position: 'top' },
        grid: { show: false },
    };

    return (
        <Card extra="p-5 border h-full">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                <div>
                    <h4 className="text-lg font-bold text-navy-700">
                        Prospects & conversion
                    </h4>
                    <p className="text-sm text-gray-500">
                        Nouveaux prospects, conversions et taux de transformation
                    </p>
                </div>
                <div className="flex gap-3 text-sm">
                    {GRANULARITIES.map((g) => (
                        <button
                            key={g.value}
                            onClick={() => setGranularity(g.value)}
                            className={`cursor-pointer ${
                                granularity === g.value
                                    ? 'font-bold text-toola-500 underline'
                                    : 'text-gray-500'
                            }`}
                        >
                            {g.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mb-4 grid grid-cols-3 gap-3">
                <div className="rounded-2xl border bg-white px-4 py-3">
                    <p className="text-xs font-medium text-gray-600">Prospects</p>
                    <h3 className="text-lg font-bold text-navy-700">
                        {formatNumber(summary?.totalProspects)}
                    </h3>
                </div>
                <div className="rounded-2xl border bg-white px-4 py-3">
                    <p className="text-xs font-medium text-gray-600">Convertis</p>
                    <h3 className="text-lg font-bold text-navy-700">
                        {formatNumber(summary?.totalConverted)}
                    </h3>
                </div>
                <div className="rounded-2xl border bg-white px-4 py-3">
                    <p className="text-xs font-medium text-gray-600">Taux de transfo.</p>
                    <h3 className="text-lg font-bold text-green-600">
                        {formatPercent(summary?.conversionRate)}
                    </h3>
                </div>
            </div>

            {loading ? (
                <div className="flex h-[280px] items-center justify-center">
                    <p className="text-gray-400">Chargement...</p>
                </div>
            ) : rows.length === 0 ? (
                <div className="flex h-[280px] items-center justify-center">
                    <p className="text-gray-400">Aucune donnée sur la période.</p>
                </div>
            ) : (
                <ReactApexChart options={options} series={series} type="line" height={300} />
            )}
        </Card>
    );
}

export default ProspectFunnel;
